// src/features/FactoryDashboard/FactoryDashboard.js
import React, { useState, useEffect } from 'react';
import Logout from '../../components/Logout';
import Button from '../../components/Button';
import './styles.css';

function FactoryDashboard() {
  const [chemicals, setChemicals] = useState([]);
  const [selectedChemical, setSelectedChemical] = useState(null);
  const [drums, setDrums] = useState([]);
  const [error, setError] = useState('');
  const [newWeight, setNewWeight] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });

    setTimeout(() => {
      setNotification(null);
    }, 4000);
  };

  const isLowStock = (chemical) =>
    chemical.threshold !== null &&
    chemical.threshold !== undefined &&
    chemical.total_weight < chemical.threshold;

  const filteredChemicals = chemicals
    .filter(c =>
      c.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
    )
    .sort((a, b) => {
      const aLow = isLowStock(a);
      const bLow = isLowStock(b);

      if (aLow && !bLow) return -1;
      if (!aLow && bLow) return 1;

      return a.name.localeCompare(b.name);
    });

  const selectedChemicalData = chemicals.find(
    c => c.id === selectedChemical
  );

  useEffect(() => {
    const storedUser = JSON.parse(sessionStorage.getItem('user'));

    if (!storedUser) {
      window.location.href = '/login';
      return;
    }

    if (storedUser.role !== 'factory') {
      window.location.href =
        storedUser.role === 'team_lead' ? '/team-lead' : '/login';
      return;
    }

    fetchChemicals();
  }, []);

  const fetchChemicals = async () => {
    try {
      const response = await fetch('https://crtapp.xyz/api/inventory', {
        cache: 'no-store',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to load chemicals');
      }

      const data = await response.json();
      setChemicals(data);
    } catch (err) {
      console.error(err);
      setError('Could not load chemicals. Please try again.');
      showNotification('Could not load chemicals.', 'error');
    }
  };

  const handleChemicalClick = async (chemical) => {
    try {
      const response = await fetch(
        `https://crtapp.xyz/api/chemicals/${chemical.id}/drums`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('Failed to load inventory items');
      }

      const data = await response.json();
      setSelectedChemical(chemical.id);
      setDrums(data);
    } catch (err) {
      console.error(err);
      setError('Could not load inventory items. Please try again.');
      showNotification('Could not load inventory items.', 'error');
    }
  };

  const handleWeightChange = (drumId, value) => {
    setNewWeight(prev => ({
      ...prev,
      [drumId]: value
    }));
  };

  const handleSaveClick = (drumId, weightValue) => {
    const newWeightNumber = parseFloat(weightValue);

    if (Number.isNaN(newWeightNumber) || newWeightNumber < 0) {
      showNotification('Please enter a valid weight of 0 or greater.', 'error');
      return;
    }

    const drum = drums.find(d => d.id === drumId);

    if (!drum) {
      showNotification('Inventory item not found.', 'error');
      return;
    }

    if (newWeightNumber > drum.weight) {
      showNotification('New weight cannot be greater than current weight.', 'error');
      return;
    }

    setConfirmData({ drumId, weight: newWeightNumber });
    setShowConfirm(true);
  };

  const handleConfirmUpdate = async () => {
    const { drumId, weight } = confirmData;

    try {
      const response = await fetch(`https://crtapp.xyz/api/drums/${drumId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ weight: Number(weight) })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update inventory item');
      }

      setDrums(prevDrums =>
        prevDrums.map(drum =>
          drum.id === drumId ? { ...drum, weight: Number(weight) } : drum
        )
      );

      setNewWeight(prev => ({
        ...prev,
        [drumId]: ''
      }));

      await fetchChemicals();

      showNotification('Inventory item updated successfully.', 'success');
    } catch (error) {
      console.error('Error updating inventory item:', error);
      showNotification(`Error updating item: ${error.message}`, 'error');
    } finally {
      setShowConfirm(false);
      setConfirmData({});
    }
  };

  return (
    <div className="factory-dashboard">
      {notification && (
        <div className={`toast ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <h1>Factory Dashboard</h1>

      {error && <p className="error">{error}</p>}

      <Logout />

      <div className="search-container">
        <input
          type="text"
          placeholder="Search chemical code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Search chemical code"
        />
      </div>

      <div className="factory-layout">
        <div className="factory-sidebar">
          <h2>Chemicals</h2>

          <ul className="chemical-list">
            {filteredChemicals.length > 0 ? (
              filteredChemicals.map((chemical) => (
                <li
                  key={chemical.id}
                  onClick={() => handleChemicalClick(chemical)}
                  className={`chemical-item ${
                    selectedChemical === chemical.id ? 'selected' : ''
                  } ${isLowStock(chemical) ? 'low-stock-item' : ''}`}
                >
                  <div>
                    <strong>
                      {chemical.name}
                      {isLowStock(chemical) && ' ⚠️'}
                    </strong>
                    <br />
                    <small>
                      📍 {chemical.location || 'Location not assigned'}
                      <br />
                      ⚖️ {chemical.total_weight} lbs
                    </small>
                  </div>
                </li>
              ))
            ) : (
              <p className="empty-message">No chemicals found.</p>
            )}
          </ul>
        </div>

        <div className="factory-details">
          {selectedChemicalData ? (
            <div className="card">
              <h2 className="selected-chemical-header">
                Working on: {selectedChemicalData.name}
              </h2>

              <p>
                <strong>Location:</strong>{' '}
                {selectedChemicalData.location || 'Location not assigned'}
              </p>

              <p>
                <strong>Total Weight:</strong> {selectedChemicalData.total_weight} lbs
              </p>

              {isLowStock(selectedChemicalData) && (
                <p className="low-stock-warning">
                  ⚠️ This chemical is below its threshold.
                </p>
              )}

              <h3>Inventory Items</h3>

              {drums.length > 0 ? (
                <ul className="drum-list">
                  {drums.map((drum) => (
                    <li key={drum.id}>
                      <span>
                        <strong>{drum.container_type || 'Drum'}</strong>
                        {' | '}
                        Lot/ID: {drum.lot_number || drum.id}
                        {' | '}
                        Weight: {drum.weight} lbs
                      </span>

                      <input
                        type="number"
                        value={newWeight[drum.id] || ''}
                        placeholder="New weight"
                        min="0"
                        step="0.01"
                        onChange={(e) => handleWeightChange(drum.id, e.target.value)}
                      />

                      <Button onClick={() => handleSaveClick(drum.id, newWeight[drum.id])}>
                        Save
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="empty-message">No inventory items for this chemical.</p>
              )}
            </div>
          ) : (
            <p>Please select a chemical to view its inventory items.</p>
          )}
        </div>
      </div>

      {showConfirm && (
        <div className="modal">
          <div className="modal-content">
            <p>
              Are you sure you want to update this item to {confirmData.weight} lbs?
            </p>

            <Button onClick={handleConfirmUpdate}>Yes, Confirm</Button>
            <Button onClick={() => setShowConfirm(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FactoryDashboard;